import * as React from 'react';
import classNames from 'classnames';
import defaultImage from '@/assets/images/defaultImage.jpg';
import { MediaInfo } from '@/types/media';
import styles from './Image.module.scss';

export interface ImageProps {
	readonly className?: string;
	readonly size180?: MediaInfo;
	readonly size320?: MediaInfo;
	readonly size480?: MediaInfo;
	readonly size720?: MediaInfo;
	readonly size960?: MediaInfo;
	readonly size1280?: MediaInfo;
	readonly size1920?: MediaInfo;
	readonly responsiveSizesSettings?: string;
	readonly altText: string;
	readonly lazyLoading?: boolean;
	readonly width?: string | number;
	readonly height?: string | number;
}

/**
 * Generic image component with optional additional responsive and retina assets
 */
export const Image: React.FunctionComponent<ImageProps> = ({
	className,
	size180,
	size320,
	size480,
	size720,
	size960,
	size1280,
	size1920,
	responsiveSizesSettings,
	altText,
	lazyLoading,
	width,
	height,
}) => {
	const isBase64 = React.useMemo(() => {
		const url = size180?.url;
		if (url) {
			return url.includes('base64');
		}
		return true;
	}, [size180?.url]);

	function resolveUrl(mediaInfo: MediaInfo | undefined, size: string | undefined): string {
		const url = mediaInfo?.url ? mediaInfo.url.replace(/\s/g, '%20') : defaultImage;
		return size ? url + ` ${size}` : url;
	}

	const urls = [
		resolveUrl(size1920, '1920w'),
		resolveUrl(size1280, '1280w'),
		resolveUrl(size960, '960w'),
		resolveUrl(size720, '720w'),
		resolveUrl(size480, '480w'),
		resolveUrl(size320, '320w'),
		resolveUrl(size180, '180w'),
	];

	return (
		<img
			className={classNames(className, { [styles.defaultImage]: isBase64 || !size180?.url })}
			src={resolveUrl(size180, undefined)}
			srcSet={`
				${urls.join(', ')}
			`}
			sizes={responsiveSizesSettings ?? '100vw'}
			alt={altText}
			// Width and height values are only used to calculate aspect ratio.
			// If values are set too small, they may limit the image size. But
			// on the other hand, if they are set too large, they will not
			// increase the image size, as the image size will be limited by the
			// container size.
			width={width !== undefined ? width : size180?.width ? size180.width * 10 : 1800}
			height={height !== undefined ? height : size180?.height ? size180.height * 10 : 4090}
			loading={lazyLoading ? 'lazy' : undefined}
		/>
	);
};
